import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

// What this admin can reach, in one call.
//
// The backend carries the whole model: areas live on admins.areas, NULL means
// every area, and my_admin_access() resolves it. This file only asks and
// caches. If you find yourself deciding permissions here, it belongs in the
// database — the sidebar is not a security boundary, RLS is.
//
// TWO RULES THIS FILE EXISTS TO HOLD:
//
// 1. IT FAILS OPEN. If the call errors — offline, a cold start, a deploy
//    mid-request — the admin keeps the access they had. A permissions layer
//    that hides the sidebar on a network blip is worse than no permissions
//    layer, because the person cannot even reach the screen that would fix it.
//    Nothing is protected by hiding it anyway: every one of these screens is
//    behind RLS on the server.
//
// 2. UNRESTRICTED IS NOT "EVERY AREA LISTED". The backend reports `unrestricted`
//    separately and can(): short-circuits on it, so a future area added to the
//    catalogue is reachable by today's admins without a migration to
//    back-fill everybody's array.

let cache = null;          // the resolved access object
let inflight = null;       // de-dupes the concurrent mount of layout + page

const UNRESTRICTED = { is_admin: true, unrestricted: true, areas: [], can_manage_team: true };

async function fetchAccess() {
  const { data, error } = await supabase.rpc('my_admin_access');
  if (error) throw error;
  return data || UNRESTRICTED;
}

export function primeAdminAccess(value) { cache = value; }
export function clearAdminAccess() { cache = null; inflight = null; }

export function useAdminAccess() {
  const [access, setAccess] = useState(cache);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let dead = false;
    if (cache) { setAccess(cache); setLoading(false); return; }
    if (!inflight) inflight = fetchAccess();
    inflight
      .then((v) => { cache = v; if (!dead) { setAccess(v); setLoading(false); } })
      .catch(() => {
        // Rule 1. Do not cache the failure — the next mount should try again.
        inflight = null;
        if (!dead) { setAccess(UNRESTRICTED); setLoading(false); }
      });
    return () => { dead = true; };
  }, []);

  const a = access || UNRESTRICTED;
  const can = (area) => a.unrestricted === true || (a.areas || []).includes(area);

  return {
    loading,
    isAdmin: a.is_admin !== false,
    unrestricted: a.unrestricted === true,
    areas: a.areas || [],
    canManageTeam: a.unrestricted === true || a.can_manage_team === true,
    can,
  };
}
